import { useCallback, useContext } from 'react'
import styled from 'styled-components'

import { TypeStyle, TypeStyleBodySmall, untilMd } from '@syconium/little-miss-figgy'

import { ProductCatalogEmbroideryTypes } from '../../../../__generated__/graphql/catalog/graphql'
import { VariantSelectionFormContext } from '../../../../app/_components/forms/VariantSelectionForm'
import { useFeatureFlag } from '../../../../app/_providers/ExperimentationProvider.client'
import { useTranslation } from '../../../../app/_providers/TranslationProvider.client'
import { useEmbroidery } from '../../../../containers/EmbroideryContainer/EmbroideryProvider'
import {
  getConflictingEmbroideryType,
  getInitialFormForEmbroideryType,
} from '../../../../containers/EmbroideryContainer/utils/embroideryFormUtils'
import { B2bLogoSneakPeek } from '../../EmbroiderySneakPeek/B2bLogoSneakPeek'

import { EmbroideryColorSelector } from './EmbroideryColorSelector'
import { EmbroideryOptionComponents } from './EmbroideryOption'
import { EmbroiderySizeOptions } from './EmbroiderySizeOptions'

export const EmbroideryOptionList = () => {
  const {
    embroideryPriceRangeByType,
    embroiderySelectionErrors,
    hasAttemptedConfirm,
    productEmbroideryOptionKeys,
    setSelectedFormKind,
    requiredEmbroideryTypes,
    productColorHex,
    withPricing,
    defaultEmbroiderySelections,
    isEmbroideryTypeSelectedInModal,
    isB2bEmbroidery,
    isTeamStoreEmbroidery,
    disabledFormFields,
  } = useEmbroidery()

  // Do not infer lock from value presence; use disabled fields.
  const isLogoPlacementLocked = disabledFormFields?.LOGO_B2B?.includes('logoPlacement') ?? false
  // Logo is editable only for team stores where placement is not locked by a defaultPlacement.
  // Portals have a fully pre-configured logo — the member cannot change anything.
  const isLogoEditable = isTeamStoreEmbroidery && !isLogoPlacementLocked

  const {
    embroidery: {
      modal: strings,
      validation,
      embroideryFormKindTitles,
      from: logoPricePrefix,
      edit: editButtonText,
      subtitle,
    },
  } = useTranslation()

  const variantSelectionFormContext = useContext(VariantSelectionFormContext)

  const showNewEmbroideryPreviewExperience =
    useFeatureFlag<'true' | 'false'>({
      key: 'new-embroidery-preview',
      controlVariant: 'false',
      ifAccessedPriorToDecisionInitialization: 'return-null-while-pending',
      skip: !variantSelectionFormContext,
    }) === 'true'

  const disabledText: Partial<Record<ProductCatalogEmbroideryTypes, string>> = {
    ICON: strings.iconUnavailable,
    LOGO: strings.logoUnavailable,
  }

  const priceRangePrefix = useCallback(
    (pricing: (typeof embroideryPriceRangeByType)[keyof typeof embroideryPriceRangeByType]) => {
      // If the price is a range, we need the prefix of "From"
      if (pricing?.max.amount !== pricing?.min.amount) {
        return <>{logoPricePrefix + ' '}</>
      }
      return null
    },
    [logoPricePrefix]
  )

  const showEmbroiderySizeOptions = Boolean(variantSelectionFormContext)

  return (
    <>
      <EmbroideryOptionComponents.List>
        {showNewEmbroideryPreviewExperience && <EmbroideryColorSelector />}
        {showEmbroiderySizeOptions && <EmbroiderySizeOptions />}
        {productEmbroideryOptionKeys.map((embroideryType, index) => {
          const pricing = embroideryPriceRangeByType[embroideryType]
          const initialFormKind = getInitialFormForEmbroideryType({
            embroideryType,
            isB2b: isB2bEmbroidery,
          })

          const conflictingType = getConflictingEmbroideryType(embroideryType)
          const isDefaultLocked =
            Boolean(defaultEmbroiderySelections?.[initialFormKind]) &&
            !(embroideryType === 'LOGO' && isLogoEditable)

          const disabled =
            Boolean(conflictingType && isEmbroideryTypeSelectedInModal(conflictingType)) ||
            isDefaultLocked

          const selected = isEmbroideryTypeSelectedInModal(embroideryType)

          const subtitleText = selected ? editButtonText : subtitle

          // Indicate required embroidery options
          const required = requiredEmbroideryTypes.includes(embroideryType)

          // Technically this doesn't catch errors if they were stored on a 2/2 step form.
          const error = hasAttemptedConfirm ? embroiderySelectionErrors[initialFormKind] : undefined

          const title = embroideryFormKindTitles[initialFormKind]
          const label = `${title}${required ? ` (${validation.required})` : ''}`

          // Show static sneak peek when the logo is not editable (locked placement or portal).
          if (embroideryType === 'LOGO' && defaultEmbroiderySelections?.LOGO_B2B && !isLogoEditable)
            return (
              <StyledLogoSneakPeek
                key={label}
                data-testid='embroidery-option-logo-sneak-peek'
                backgroundColor={productColorHex}
                imgSrc={defaultEmbroiderySelections.LOGO_B2B.logoImageSource}
                logoName={defaultEmbroiderySelections.LOGO_B2B.logoName}
                logoPlacement={defaultEmbroiderySelections.LOGO_B2B.logoPlacement}
              />
            )

          return (
            <EmbroideryOptionComponents.ListOption
              key={index}
              index={index}
              kind={initialFormKind}
              handleClick={selected ? () => null : setSelectedFormKind}
              label={label}
              disabled={disabled}
              selected={selected}
              error={
                error ? (
                  <EmbroideryOptionComponents.Error
                    id={`${initialFormKind}_formerror`}
                    message={error}
                  />
                ) : null
              }
              errorMessageId={`${initialFormKind}_formerror`}
            >
              <LeftContent data-testid='embroidery-option-styled-selector' data-selected={selected}>
                <ListOptionTitle $disabled={disabled} $isInvalid={Boolean(error)}>
                  {label}
                </ListOptionTitle>
                <ListOptionSubtitle>
                  {selected ? (
                    <>
                      <ListOptionEditButton
                        aria-label={editButtonText}
                        onClick={() => setSelectedFormKind(initialFormKind)}
                      >
                        {subtitleText}
                      </ListOptionEditButton>
                      {withPricing && pricing && (
                        <span> +{pricing.effectivePrice.displayValue}</span>
                      )}
                    </>
                  ) : (
                    !disabled && <span>{subtitle}</span>
                  )}
                </ListOptionSubtitle>
              </LeftContent>
              {disabled ? (
                <DisabledMessage>{disabledText[embroideryType] ?? 'Not available'}</DisabledMessage>
              ) : (
                <EmbroideryOptionComponents.RightContent $disabled={disabled}>
                  {withPricing && pricing && !selected && (
                    <span aria-hidden='true'>
                      {priceRangePrefix(pricing)}+{pricing.min.displayValue}
                    </span>
                  )}
                  <EmbroideryOptionComponents.Icon
                    selected={selected}
                    embroideryType={embroideryType}
                  />
                </EmbroideryOptionComponents.RightContent>
              )}
            </EmbroideryOptionComponents.ListOption>
          )
        })}
      </EmbroideryOptionComponents.List>
    </>
  )
}

const LeftContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`

const ListOptionTitle = styled.div<{ $disabled?: boolean; $isInvalid?: boolean }>`
  ${TypeStyle.css.headlineMicro}
  text-transform: uppercase;
  color: ${o =>
    o.$disabled
      ? o.theme.color.stroke.disabled.text.on.background
      : o.$isInvalid
      ? o.theme.color.text.critical.on.background
      : o.theme.color.text.primary.on.background};
  letter-spacing: ${o => o.theme.typography.font.letterSpacing.wide};
  font-weight: ${o => o.theme.typography.font.weight[700]};
  line-height: ${o => o.theme.typography.font.lineHeight.loose};
`
const ListOptionSubtitle = styled.div`
  color: ${o => o.theme.color.text.secondary.on.background};
`

const ListOptionEditButton = styled.button`
  text-decoration: underline;
  cursor: pointer;
  font-weight: ${o => o.theme.typography.font.weight[600]};
`

const StyledLogoSneakPeek = styled(B2bLogoSneakPeek)`
  margin-top: ${o => o.theme.spacing(4)};
`

const DisabledMessage = styled(TypeStyleBodySmall)`
  font-style: italic;
  color: ${o => o.theme.color.deprecated.stoneDark};
  font-weight: ${o => o.theme.typography.font.weight[600]};
  line-height: 20px;
  letter-spacing: 0.6px;
  align-self: center;

  ${untilMd} {
    font-size: 10px;
    font-weight: ${o => o.theme.typography.font.weight[600]};
    line-height: 12px;
    letter-spacing: 0.4px;
  }
`
